import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox } from 'antd';
import { fetchLicenseList } from '../../pages/product/license-form/licenseApi';
import './RoleAndCategorySelector.css';

function mapApiLicenses(licenses) {
  return (licenses || [])
    .map((item) => ({
      id: item.license_id || item.id,
      name: item.license_name || item.name,
    }))
    .filter((item) => item.id && item.name);
}

function RoleAndCategorySelector({ userItem, setUserItem, appHostname, token }) {
  const [apiLicenses, setApiLicenses] = useState([]);
  const [optionsReady, setOptionsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadLicenses() {
      setLoading(true);
      setOptionsReady(false);
      setLoadError('');
      try {
        const data = await fetchLicenseList({
          appHostname,
          token,
        });
        if (cancelled) return;
        if (data?.status === 'OK') {
          setApiLicenses(mapApiLicenses(data.licenses));
          setOptionsReady(true);
        } else {
          setApiLicenses([]);
          setOptionsReady(false);
          setLoadError(data?.message || 'โหลดใบทะเบียนไม่สำเร็จ');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load shop license options', err);
          setApiLicenses([]);
          setOptionsReady(false);
          setLoadError(
            err?.response?.data?.message || err.message || 'เชื่อมต่อเซิร์ฟเวอร์ล้มเหลว',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadLicenses();
    return () => {
      cancelled = true;
    };
  }, [appHostname, token]);

  const licenses = apiLicenses;
  const selectedIds = useMemo(() => userItem?.sell_license_ids || [], [userItem?.sell_license_ids]);
  const licenseIdsKey = useMemo(() => licenses.map((item) => item.id).join('\0'), [licenses]);

  useEffect(() => {
    if (!optionsReady || loading) {
      return;
    }
    const visibleIds = new Set(licenseIdsKey === '' ? [] : licenseIdsKey.split('\0'));
    const nextIds = selectedIds.filter((id) => visibleIds.has(id));
    const same =
      nextIds.length === selectedIds.length &&
      nextIds.every((id, index) => id === selectedIds[index]);
    if (!same && typeof setUserItem === 'function') {
      setUserItem({ sell_license_ids: nextIds });
    }
  }, [optionsReady, loading, licenseIdsKey, selectedIds, setUserItem]);

  const handleToggle = (licenseId, checked) => {
    const visibleIds = new Set(licenses.map((license) => license.id));
    const base = selectedIds.filter((id) => visibleIds.has(id));
    const nextIds = checked
      ? [...new Set([...base, licenseId])]
      : base.filter((id) => id !== licenseId);

    if (typeof setUserItem === 'function') {
      setUserItem({ sell_license_ids: nextIds });
    }
  };

  return (
    <div className='shop-license-selector'>
      <h3 className='shop-license-selector__title'>เลือกกำหนดสิทธิ์ตามใบทะเบียน/ประเภทธุรกิจ</h3>
      <div className='shop-license-selector__grid'>
        {loading ? (
          <div className='shop-license-selector__empty'>กำลังโหลดใบทะเบียน...</div>
        ) : loadError ? (
          <div className='shop-license-selector__empty'>{loadError}</div>
        ) : licenses.length === 0 ? (
          <div className='shop-license-selector__empty'>ยังไม่มีใบทะเบียนในระบบ</div>
        ) : (
          licenses.map((license) => (
            <Checkbox
              key={license.id}
              className='shop-license-selector__card'
              checked={selectedIds.includes(license.id)}
              onChange={(event) => handleToggle(license.id, event.target.checked)}
            >
              {license.name}
            </Checkbox>
          ))
        )}
      </div>
    </div>
  );
}

RoleAndCategorySelector.propTypes = {
  userItem: PropTypes.shape({
    id: PropTypes.string,
    shop_id: PropTypes.string,
    sell_license_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  setUserItem: PropTypes.func,
  onCategoryChange: PropTypes.func,
  appHostname: PropTypes.string,
  token: PropTypes.string,
};

RoleAndCategorySelector.defaultProps = {
  userItem: {
    sell_license_ids: [],
  },
  setUserItem: () => {},
  onCategoryChange: null,
  appHostname: '',
  token: '',
};

export default RoleAndCategorySelector;
